import SupportIcon from '@assets/icons/live-chat.svg?react'
import cn from 'classnames'
import { paths, useGetUnreadTicketsQuery } from 'mmfintech-backend-api'
import type { ClientUnreadTicketResponse } from 'mmfintech-backend-api/types'
import { GlobalContext, tr } from 'mmfintech-commons'
import moment from 'moment'
import { type KeyboardEvent, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import styles from './NotificationsPanel.module.scss'

const MAX_TICKETS = 3

const fillCount = (template: string, count: number) => template.replace('{{COUNT}}', String(count))

const onActivate = (action: () => void) => (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    action()
  }
}

const getPreview = (ticket: ClientUnreadTicketResponse): string | null => {
  const message = ticket.lastUnreadMessage
  if (!message) {
    return null
  }
  if (message.isFile) {
    return tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_SENT_FILE', 'Customer Support sent a file')
  }
  return `${tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_SENDER', 'Customer Support')}: ${message.text ?? ''}`
}

export const SupportBlock = () => {
  const { sidebarRightHide } = useContext(GlobalContext)
  const history = useHistory()
  const { data: unreadTickets } = useGetUnreadTicketsQuery()

  if (!unreadTickets?.length) {
    return null
  }

  const visibleTickets = unreadTickets.slice(0, MAX_TICKETS)
  const hiddenCount = unreadTickets.length - visibleTickets.length

  const openSupport = (ticket?: ClientUnreadTicketResponse) => {
    sidebarRightHide()
    history.push(ticket?.ticketId ? paths.support.ticketDetail(ticket.ticketId) : paths.support.tickets())
  }

  return (
    <section className={cn(styles['support-block'], 'support-block')} data-test='notifications-support'>
      <div className={cn(styles['support-block-header'], 'support-block-header')}>
        <span className={cn(styles['support-block-icon'], 'support-block-icon')}>
          <SupportIcon />
        </span>
        <h3 className={cn(styles['support-block-title'], 'support-block-title')}>
          {unreadTickets.length === 1
            ? tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_TITLE_ONE', 'Support · 1 ticket with new replies')
            : fillCount(
                tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_TITLE', 'Support · {{COUNT}} tickets with new replies'),
                unreadTickets.length
              )}
        </h3>
        <span
          className={cn(styles['support-block-link'], 'support-block-link')}
          role='button'
          tabIndex={0}
          onClick={() => openSupport()}
          onKeyDown={onActivate(() => openSupport())}
          data-test='notifications-support-open'
        >
          {tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_OPEN', 'Open Support')} ›
        </span>
      </div>

      {visibleTickets.map(ticket => {
        const preview = getPreview(ticket)
        const createdAt = ticket.lastUnreadMessage?.createdAt

        return (
          <div
            key={ticket.ticketId}
            className={cn(styles['support-row'], 'support-row')}
            role='button'
            tabIndex={0}
            onClick={() => openSupport(ticket)}
            onKeyDown={onActivate(() => openSupport(ticket))}
            data-test={`notifications-support-ticket-${ticket.ticketId}`}
          >
            <div className={cn(styles['support-row-body'], 'support-row-body')}>
              <p className={cn(styles['support-row-title'], 'support-row-title')}>
                <span className={cn(styles['support-row-number'], 'support-row-number')}>TKT-{ticket.ticketId}</span>
                {ticket.title}
              </p>
              {preview && <p className={cn(styles['support-row-preview'], 'support-row-preview')}>{preview}</p>}
              {createdAt && (
                <p className={cn(styles['support-row-time'], 'support-row-time')}>
                  {moment(createdAt).format('HH:mm · D MMM')}
                </p>
              )}
            </div>
            {(ticket.unreadCount ?? 0) > 1 ? (
              <span className={cn(styles['support-row-pill'], 'support-row-pill')}>
                {fillCount(tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_NEW', '{{COUNT}} new'), ticket.unreadCount ?? 0)}
              </span>
            ) : (
              <span className={cn(styles['support-row-dot'], 'support-row-dot')} aria-hidden='true' />
            )}
          </div>
        )
      })}

      {hiddenCount > 0 && (
        <div
          className={cn(styles['support-more'], 'support-more')}
          role='button'
          tabIndex={0}
          onClick={() => openSupport()}
          onKeyDown={onActivate(() => openSupport())}
        >
          {hiddenCount === 1
            ? tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_MORE_ONE', '+ 1 more ticket · View all in Support')
            : fillCount(
                tr('FRONTEND.NOTIFICATIONS_PANEL.SUPPORT_MORE', '+ {{COUNT}} more tickets · View all in Support'),
                hiddenCount
              )}
        </div>
      )}
    </section>
  )
}
